import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, Card, Dialog, Spinner } from "@blueprintjs/core";

import { useCreateOrganization } from "@hooks";
import { OrgData } from "@types";
import { confirmPrompt, withToaster } from "@utils";

import {
  createSCMConfig,
  deleteSCMConfig,
  getSCMConfigs,
} from "@pages/settings/api";
import { ManageSCMPanel } from "@pages/settings/components/ManageSCMPanel";
import { SCMConfig, SCMConfigUserInput } from "@pages/settings/types";

const Container = styled.div`
  overflow: "wrap";
`;

const DescriptionContainer = styled.div`
  margin-top: var(--global-spacing-l);
  max-width: 500px;
`;

type Props = {
  org: OrgData;
};

export const ScmPanel: React.FC<Props> = ({ org }) => {
  const [SCMs, setSCMs] = useState<SCMConfig[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSCMDialogOpen, setIsSCMDialogOpen] = useState<boolean>(false);
  const parentOrgId = org.organization_id;
  const { createOrganization } = useCreateOrganization();

  useEffect(() => {
    if (!parentOrgId) return;
    let isMounted = true;
    withToaster(
      () =>
        getSCMConfigs(parentOrgId).then((response) => {
          if (!isMounted) return;
          setSCMs(response.scm_configs.sort((a, b) => b.id - a.id));
        }),
      (err) => `Failed to load SCM configuration: ${err.body?.error}`
    )().finally(() => {
      if (!isMounted) return;
      setIsLoading(false);
    });

    // Abort any callbacks/promises on component unmounting
    return () => {
      isMounted = false;
    };
  }, [parentOrgId]);

  const createButtonClick = (newSCM: SCMConfigUserInput) => {
    if (!parentOrgId) return;
    withToaster(
      () =>
        createSCMConfig(newSCM, parentOrgId)
          .then((response) => response.scm_config)
          .then((createdSCM) => {
            setIsSCMDialogOpen(false);
            setSCMs((scms) => [createdSCM].concat(scms));
          }),
      (err) => `Failed to submit SCM configuration: ${err.body?.error}`
    )();
  };

  const deleteButtonClick = (scmId: number) => {
    if (!parentOrgId) return;
    confirmPrompt("Are you sure you want to delete the SCM config?").then(() =>
      withToaster(
        () =>
          deleteSCMConfig(parentOrgId, scmId).then(() => {
            setSCMs((scms) => scms.filter((scm) => scm.id !== scmId));
          }),
        (err) => `Failed to delete SCM configuration: ${err.body?.error}`
      )()
    );
  };

  useEffect(() => {
    if (!org.id || !createOrganization || parentOrgId) return;
    createOrganization(org.id);
  }, [parentOrgId, createOrganization, org.id]);

  if (!parentOrgId) {
    return <Spinner intent="primary" />;
  }

  return (
    <Container>
      <Dialog
        isOpen={isSCMDialogOpen}
        onClose={() => {
          setIsSCMDialogOpen(false);
        }}
      >
        <ManageSCMPanel onFinish={createButtonClick} />
      </Dialog>
      <DescriptionContainer>
        <p>
          SCM (source control management) software can be integrated into
          semgrep, even when not used as an identity provider.
        </p>
        <p>
          The primary identity provider is available by default, there is no
          need to explicitly enable their platform.
        </p>
      </DescriptionContainer>
      <Button
        style={{ marginBottom: "10px", marginTop: "10px" }}
        intent={SCMs.length === 0 ? "primary" : "success"}
        onClick={async () => {
          setIsSCMDialogOpen(true);
        }}
      >
        Configure new SCM
      </Button>
      {isLoading ? (
        <Spinner intent="primary" />
      ) : (
        <div>
          {SCMs &&
            SCMs.map((scm, index) => {
              return (
                <Card
                  key={index}
                  style={{
                    display: "flex",
                    width: "fit-content",
                    marginBottom: "10px",
                  }}
                >
                  <p
                    style={{
                      width: "520px",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      marginTop: "6px",
                    }}
                  >
                    {scm.base_url} •••{" "}
                    {scm.scm_type === "github_enterprise"
                      ? "Github Enterprise"
                      : "GitLab Self-Managed"}
                  </p>
                  <Button
                    style={{
                      marginLeft: "10px",
                      marginBottom: "4px",
                      width: "50px",
                    }}
                    onClick={() => deleteButtonClick(scm.id)}
                    intent="danger"
                    minimal
                  >
                    delete
                  </Button>
                </Card>
              );
            })}
        </div>
      )}
    </Container>
  );
};
